import {
  alertError,
  alertSuccess,
  AsyncButton,
  Button,
  ButtonStyle,
  capitalize,
  colors,
  GiftItemStatus,
  GiftItemType,
  IGiftItemResponse,
  IGiftVoucher,
  ISpecialOffer,
  ITicket,
  Loading,
  stripHTML,
  IPromotionResponse,
} from '@one-tree/library'
import React, { ReactElement, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components'
import Embed from '../../../components/Embed'
import Card, { CardSize } from '../../../components/page/Card'
import CardContent from '../../../components/page/CardContent'
import CardHeader from '../../../components/page/CardHeader'
import { useOrganisation } from '../../../context/OrganisationProvider'
import { get, getOrgAccessToken } from '../../../helpers/APIHelper'
import { ICategoryResponse, Resource } from '../../../types/API'
import {
  createGiftItem,
  deleteGiftItem,
  getGiftItem,
  patchGiftItem,
} from '../../../helpers/APIItemHelper'
import {
  Code,
  itemWord,
  shortVoucherWord,
  urlWord,
  voucherWord,
} from '../../../helpers/FormatHelper'
import { getActualChanges } from '../../../helpers/GiftItemHelper'
import { isGiftItemValid } from '../../../helpers/GiftItemValidityHelper'
import Page from '../../../hoc/Page'
import {
  BulkQuestionReorder,
  IGiftItemPostBody,
} from '../../../types/GiftItemTypes'
import { RoutePath } from '../../../types/Routes'
import { OrganisationFormat } from '../../../types/Types'
import { convertPromoResponseToPromotion, Promotions } from '../promotion/PromotionHelper'
import {
  normalizeReorders,
  getPriceChanges,
  saveQuestionOrder,
  updatePriceQuestions,
} from './designElements/TicketPrices/pricesHelper'
import GiftVoucherDesign from './GiftVoucherDesign'
import SpecialOfferDesign from './SpecialOfferDesign'
import TicketDesign from './TicketDesign'

const GiftGrid = styled.div`
  margin-top: 40px;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 60px 20px;
  grid-template-areas:
    'gift-settings gift-settings'
    'gift-design preview-design'
    'gift-description preview-description';
  section {
    display: flex;
    flex-direction: column;
    gap: 30px 0;
    h3 {
      margin: 0;
    }
  }
  .gift-settings {
    grid-area: gift-settings;
  }
  /* Breaking these styles out of the above class as Ticket Questions looks better without */
  .gift-settings-styles {
    display: flex;
    flex-direction: column;
    gap: 30px 0;
    h3 {
      margin: 0;
    }
    border: 1px solid ${colors.lightGray};
    border-radius: 4px;
    background-color: ${colors.lightestGray};
    padding: 20px;
    p {
      margin: 0;
    }
  }
  .gift-design {
    grid-area: gift-design;
    min-width: 320px;
  }
  .preview-design {
    grid-area: preview-design;
  }
  .gift-description {
    grid-area: gift-description;
  }
  .preview-description {
    grid-area: preview-description;
  }
`

function EditGiftItem(): ReactElement {
  const { id }: { id: string } = useParams()
  const history = useHistory()
  const [giftItem, setGiftItem] = useState<IGiftItemResponse | null>(null)
  const [changes, setChanges] = useState<IGiftItemPostBody | null>(null)
  const [loading, setLoading] = useState(false)
  const [showEmbed, setShowEmbed] = useState(false)
  const [isEditing, setIsEditing] = useState(Boolean(id))
  const [processing, setProcessing] = useState(false)
  const [promotions, setPromotions] = useState<Promotions>()
  const [questionReorders, setQuestionReorders] = useState<BulkQuestionReorder[]>([])
  const [categories, setCategories] = useState<ICategoryResponse[]>()

  const { organisation } = useOrganisation()
  const format = organisation?.format
  const wording = itemWord(organisation)
  const wordingCaps = capitalize(wording)
  const longVoucher = voucherWord(organisation)
  const shortVoucher = shortVoucherWord(organisation)
  const isTESO = format !== OrganisationFormat.GiftVouchers

  const monetary = !isTESO
    ? ', unless the Monetary option is selected. In this case the Summary field does not appear on the voucher'
    : ''

  const text = `The words entered in these fields appear on your website and on the ${longVoucher}${monetary}. Please describe what is included. General terms and conditions of use appear at the bottom of all ${longVoucher}s and can be edited in the ${shortVoucher} template.`

  const fetchItem = async (): Promise<void> => {
    setLoading(true)

    const [itemResponse, promoResponse, categoriesResponse] = await Promise.all([
      id && organisation
        ? getGiftItem({ format: organisation.format, id })
        : Promise.resolve(null),
      organisation?.hasPromotions
        ? get<IPromotionResponse[]>({ resource: Resource.Promotions })
        : Promise.resolve(null),
      organisation?.categoriesEnabled
        ? get<ICategoryResponse[]>({ resource: Resource.Categories })
        : Promise.resolve(null),
    ])

    if (id && organisation) {
      if (itemResponse) {
        setGiftItem(itemResponse)
      } else {
        alertError(`${wordingCaps} not found`)
        history.push(RoutePath.Home)
      }
    }

    if (promoResponse) {
      setPromotions(promoResponse.map(convertPromoResponseToPromotion))
    }
    if (categoriesResponse) setCategories(categoriesResponse)

    setLoading(false)
  }

  const refetchGiftItem = async (): Promise<void> => {
    if (!id || !organisation) return
    const itemResponse = await getGiftItem({ format: organisation.format, id })
    if (itemResponse) setGiftItem(itemResponse)
  }

  useEffect(() => {
    fetchItem()
  }, [id])

  useEffect(() => {
    if (!id && !giftItem) {
      setGiftItem((prevState) => ({
        ...prevState,
        status: GiftItemStatus.Unpublished,
      }))
    }
  }, [])

  useEffect(() => {
    // if user changes to a Monetary type, description should auto-fill with copy
    const existingDescription = stripHTML(
      (changes?.description || '') + (giftItem?.description || ''),
    )
    if (changes?.type === GiftItemType.Monetary && !existingDescription) {
      setChanges((prevState) => ({
        ...prevState,
        description:
          'Give a special treat. Choose an amount, personalise and buy your gift voucher securely today.',
      }))
    }
  }, [changes?.type])

  const handleArchiveItem = async (): Promise<void> => {
    setProcessing(true)
    if (giftItem && giftItem.id && organisation) {
      const response = await deleteGiftItem({
        format: organisation.format,
        id: giftItem.id,
      })
      if (response) {
        alertSuccess(`${wordingCaps} archived`)
        history.goBack()
      }
    }
    setProcessing(false)
  }

  const handleRestoreItem = async (): Promise<void> => {
    setProcessing(true)
    if (giftItem && giftItem.id && organisation) {
      const response = await patchGiftItem({
        format: organisation.format,
        itemId: giftItem.id,
        patchOptions: {
          status: GiftItemStatus.Unpublished,
        },
      })
      if (response) {
        alertSuccess(`${wordingCaps} restored`)
        history.goBack()
      }
    }
    setProcessing(false)
  }

  const handleCloneItem = async (): Promise<void> => {
    if (giftItem && organisation) {
      const giftItemClone: Partial<IGiftItemResponse> = { ...giftItem }
      // delete fields that cannot be given to a clone (i.e. clone will have own `id`, `order`, etc)
      delete giftItemClone.id
      delete giftItemClone.imageUrl
      delete giftItemClone.headerImageUrl
      delete giftItemClone.quantitySold
      delete giftItemClone.revenueSold
      delete giftItemClone.status
      delete giftItemClone.order
      delete giftItemClone.embedUrl
      delete giftItemClone.enabledQuestionIds
      if (giftItemClone.prices) {
        giftItemClone.prices = giftItemClone.prices.map((price) => ({
          ...price,
          enabledQuestionIds: [],
        }))
      }
      const response = await createGiftItem({
        format: organisation.format,
        giftItem: giftItemClone,
      })
      setLoading(true)
      if (response) {
        // Timeout gives user visual feedback that the Item has changed
        setTimeout(() => {
          alertSuccess(`${wordingCaps} cloned`)
          history.push(`${RoutePath.ItemGrid}/${response.id}`)
          setLoading(false)
        }, 1000)
      } else {
        alertError(`There was an error cloning the ${wording}`)
        setLoading(false)
      }
    }
  }

  const handlePreview = async (giftId: number | undefined): Promise<void> => {
    const token = await getOrgAccessToken()
    if (giftId && token && process.env.REACT_APP_BACKEND_URL) {
      window.open(
        `${process.env.REACT_APP_BACKEND_URL}/gift_items/preview/${giftId}?token=${token}`,
        '_blank',
      )
    }
  }

  const handleSaveItem = async (): Promise<void> => {
    setProcessing(true)

    if (giftItem && organisation) {
      const hasNoHeader = !giftItem.headerImageUrl
      const hasNoNewHeader = !changes?.headerImageFileId
      const addDefaultHeader = hasNoHeader
        && hasNoNewHeader && {
          headerImageFileId: organisation.headerImageFileId,
        }
      const toSave = {
        ...changes,
        ...addDefaultHeader,
      }

      // Optional params that can be cleared by the user set to null, since Axios strips undefined
      if (changes) {
        if (
          Object.keys(changes).includes('originalPrice')
          && changes.originalPrice === undefined
        ) {
          toSave.originalPrice = null
        }
      }

      // If listing-only is enabled, clear any associated questions
      if (toSave.listingOnly === true) {
        toSave.enabledQuestionIds = []
        if (toSave.prices) {
          toSave.prices = toSave.prices.map((price) => ({
            ...price,
            enabledQuestionIds: [],
          }))
        }
      }

      // Extract Ticket Prices from the changes so they can be handled separately
      const { prices: newPrices } = toSave
      delete toSave.prices

      const pricesSuccess = await updatePriceQuestions(
        organisation,
        getPriceChanges(newPrices, giftItem?.prices),
      )

      let itemSuccess: IGiftItemResponse | false = false
      // If we still have changes after removing Ticket Prices
      const stillHasChanges = Object.keys(toSave).length !== 0

      if (stillHasChanges) {
        if (id && giftItem.id) {
          // when patching, only send what has changed
          itemSuccess = await patchGiftItem({
            format: organisation.format,
            itemId: giftItem.id,
            patchOptions: toSave,
          })
        } else {
          itemSuccess = await createGiftItem({
            format: organisation.format,
            giftItem: toSave,
          })
        }
      }

      // Save any question reorders
      const filteredReorders = normalizeReorders(
        questionReorders,
        giftItem,
        changes,
      )
      const reordersSuccess = await saveQuestionOrder(
        organisation,
        filteredReorders,
      )

      if (itemSuccess || pricesSuccess || reordersSuccess) {
        alertSuccess(`${wordingCaps} saved`)
        setLoading(true)

        // open the preview on save if the gift item is valid
        if (
          itemSuccess
          && isGiftItemValid(itemSuccess, organisation, undefined, categories)
        ) {
          handlePreview(itemSuccess.id)
        }

        setChanges(null)
        setQuestionReorders([])
        // Use setTimeout to ensure state updates are processed before navigating
        setTimeout(() => {
          history.goBack()
        }, 0)
      } else {
        setLoading(false)
      }
    }
    setProcessing(false)
  }

  const handleChangeGiftItem = (updates: IGiftItemPostBody): void => {
    setChanges({
      ...changes,
      ...updates,
    })
  }

  const actualChanges = getActualChanges(
    giftItem || undefined,
    changes || undefined,
  )
  const hasUnsavedChanges = Boolean(actualChanges) || questionReorders.length > 0

  const isArchived = giftItem?.status === GiftItemStatus.Archived

  return (
    <Page warnBeforeExit={!processing && hasUnsavedChanges}>
      <Card cardSize={CardSize.Medium}>
        <CardHeader
          title={
            id
              ? `Edit${isArchived ? ' archived' : ''} ${wording}`
              : `Create new ${wording}`
          }
        >
          {!loading && (
            <>
              <Button
                buttonStyle={ButtonStyle.Secondary}
                onClick={(): void => history.goBack()}
              >
                Cancel
              </Button>
              {giftItem?.id && (
                <>
                  {!isArchived && (
                    <AsyncButton
                      buttonStyle={ButtonStyle.Secondary}
                      onClick={(): void => setShowEmbed(true)}
                    >
                      Embed
                    </AsyncButton>
                  )}
                  {isArchived ? (
                    <AsyncButton
                      buttonStyle={ButtonStyle.Action}
                      onClick={handleRestoreItem}
                    >
                      Restore
                    </AsyncButton>
                  ) : (
                    <AsyncButton
                      buttonStyle={ButtonStyle.Danger}
                      onClick={handleArchiveItem}
                      showConfirmation={true}
                      confirmationMessage={`This ${wording} will be hidden, but can be restored later. Any unsaved changes will be lost. Proceed?`}
                    >
                      Archive
                    </AsyncButton>
                  )}
                  <AsyncButton
                    buttonStyle={ButtonStyle.Primary}
                    onClick={(): Promise<void> => handleCloneItem()}
                    showConfirmation={true}
                    confirmationMessage={`Are you sure you want to clone this ${wording}?`}
                    disabled={hasUnsavedChanges}
                  >
                    Clone
                  </AsyncButton>
                  <AsyncButton
                    buttonStyle={ButtonStyle.Primary}
                    onClick={(): Promise<void> => handlePreview(giftItem.id)}
                    disabled={hasUnsavedChanges}
                  >
                    Preview
                  </AsyncButton>
                </>
              )}
              <AsyncButton
                buttonStyle={ButtonStyle.Action}
                onClick={handleSaveItem}
                disabled={!hasUnsavedChanges}
                showConfirmation={
                  !isGiftItemValid({ ...giftItem, ...changes }, organisation, undefined, categories)
                }
                confirmationMessage={`Are you sure you want to save an unfinished ${wording}?`}
                onConfirmationCancel={(): void => setIsEditing(true)}
              >
                Save
              </AsyncButton>
            </>
          )}
        </CardHeader>
        <CardContent>
          {loading || !giftItem ? (
            <Loading fullPage={true} />
          ) : (
            <>
              <p>{text}</p>
              <GiftGrid>
                {/* For the below type assertions. If we are e.g. GiftVouchers
              format, we know giftItem is IGiftVoucher  */}
                {/* eslint-disable no-type-assertion/no-type-assertion */}
                {format === OrganisationFormat.GiftVouchers && (
                  <GiftVoucherDesign
                    giftVoucher={
                      {
                        ...giftItem,
                        ...changes,
                      } as IGiftVoucher
                    }
                    changeGiftVoucher={handleChangeGiftItem}
                    isEditing={isEditing}
                    promotions={promotions}
                    onPromotionToggle={refetchGiftItem}
                  />
                )}
                {format === OrganisationFormat.SpecialOffers && (
                  <SpecialOfferDesign
                    specialOffer={
                      {
                        ...giftItem,
                        ...changes,
                      } as ISpecialOffer
                    }
                    changeSpecialOffer={handleChangeGiftItem}
                    isEditing={isEditing}
                  />
                )}
                {format === OrganisationFormat.Tickets && (
                  <TicketDesign
                    ticket={
                      {
                        ...giftItem,
                        ...changes,
                      } as ITicket
                    }
                    changeTicket={handleChangeGiftItem}
                    isEditing={isEditing}
                    setGiftItem={setGiftItem}
                    changes={changes}
                    setChanges={setChanges}
                    setQuestionReorders={setQuestionReorders}
                  />
                )}
              </GiftGrid>
            </>
          )}
        </CardContent>
      </Card>
      <Embed
        modalOpen={showEmbed}
        setModalOpen={setShowEmbed}
        embedUrl={giftItem?.embedUrl}
        linkText={(
          <>
            <br />
            <h3>Create a link to this {wording}</h3>
            <p>Add the following suffix to your {longVoucher} shop URL:</p>
            <Code>?item={giftItem?.id}</Code>
            <p>{`E.g. https://yourcompanyname.co.uk/${urlWord(organisation)}?item=XXX`}</p>
          </>
        )}
      />
    </Page>
  )
}
export default EditGiftItem
